// PathsListPanel.jsx — Hierarchical sidebar showing all independent paths and
// their lanes. Replaces PathCreatorPanelContent for the new multi-path model.

import React, { useState, useRef, useEffect } from 'react';
import { LANE_TYPE_LABELS } from './rsaPathTypes';
import { getPathLengthMiles } from './PathsPanelWaypoints';
import { SEGMENT_COLORS } from '../InternalComponents/rsaPathUtils';

// ─── Speed helpers ───────────────────────────────────────────────────────────

/** Compute total lane length in miles from waypoints or GeoJSON coords */
function getLaneLengthMiles(lane) {
  const coords = lane?.pathData?.geometry?.coordinates;
  if (coords?.length >= 2) return getPathLengthMiles(coords);
  if (lane?.waypoints?.length >= 2) return getPathLengthMiles(lane.waypoints);
  return 0;
}

/** Format mile mark: 0.00, 0.45, etc. */
function fmtMile(mi) {
  return mi.toFixed(2);
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtLength(pathData, waypoints) {
  const miles = pathData?.geometry?.coordinates?.length >= 2
    ? getPathLengthMiles(pathData.geometry.coordinates)
    : getPathLengthMiles(waypoints);
  if (miles == null) return null;
  return miles < 0.1
    ? `${(miles * 5280).toFixed(0)} ft`
    : `${miles.toFixed(2)} mi`;
}

function InlineLabel({ value, onChange, disabled = false, leadingIcon = null, forceEdit = false }) {
  const [draft, setDraft] = useState(value);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);
  const shouldRevertOnBlurRef = useRef(false);

  useEffect(() => {
    if (!isFocused) {
      setDraft(value);
    }
  }, [value, isFocused]);

  useEffect(() => {
    if (forceEdit && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [forceEdit]);

  const commitDraft = () => {
    const nextValue = draft.trim() || value;
    setDraft(nextValue);
    if (nextValue !== value) {
      onChange(nextValue);
    }
  };

  return (
    <span className="paths-list-label-text" title={disabled ? undefined : 'Edit name'}>
      {leadingIcon}
      <input
        ref={inputRef}
        className="paths-list-label-value paths-list-label-input paths-list-label-input--inline"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          setIsFocused(false);
          if (shouldRevertOnBlurRef.current) {
            shouldRevertOnBlurRef.current = false;
            setDraft(value);
            return;
          }
          commitDraft();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            inputRef.current?.blur();
          }
          if (e.key === 'Escape') {
            e.preventDefault();
            shouldRevertOnBlurRef.current = true;
            inputRef.current?.blur();
          }
        }}
        onClick={(e) => e.stopPropagation()}
        disabled={disabled}
        maxLength={60}
      />
    </span>
  );
}

// ─── Inline Offset Form ───────────────────────────────────────────────────────

function InlineOffsetForm({
  paths,
  activePathId,
  wktAvailable,
  onCreateOffsetLane,
  onCancel,
  mode = 'offset',
}) {
  const pathOptions = (paths || []).filter((p) => p.pathData?.geometry?.coordinates?.length >= 2);
  const [sourcePathId, setSourcePathId] = useState(activePathId ?? pathOptions[0]?.id ?? '');
  const [directionMode, setDirectionMode] = useState('same');
  const [side, setSide] = useState('right');
  const isCopyMode = mode === 'copy';
  const [offsetFeet, setOffsetFeet] = useState(isCopyMode ? '0' : '6');
  const [creating, setCreating] = useState(false);

  // Sync sourcePathId when activePathId changes
  useEffect(() => {
    if (activePathId && activePathId !== sourcePathId) setSourcePathId(activePathId);
  }, [activePathId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setOffsetFeet(isCopyMode ? '0' : '6');
  }, [isCopyMode]);

  const parsedOffsetFeet = Number(offsetFeet);
  const hasValidOffset =
    offsetFeet !== '' &&
    Number.isFinite(parsedOffsetFeet) &&
    parsedOffsetFeet >= 0;
  const canCreate = pathOptions.length > 0 && wktAvailable && hasValidOffset;

  async function handleCreate() {
    if (!canCreate || creating) return;
    setCreating(true);
    try {
      await onCreateOffsetLane?.({
        sourcePathId: sourcePathId || pathOptions[0]?.id,
        directionMode,
        side,
        offsetFeet: parsedOffsetFeet,
      });
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="paths-list-inline-form paths-list-inline-form--offset">
      <div className="paths-list-inline-form-header">
        <div className="paths-list-inline-form-header-copy">
          <p className="paths-list-inline-form-title">{isCopyMode ? 'Copy Lane' : 'Create Offset Lane'}</p>
          <p className="paths-list-inline-form-subtitle">
            {isCopyMode
              ? 'Create a duplicate lane from a routed path using the same geometry with 0 ft offset.'
              : 'Generate a lane from a routed path using side, offset distance, and direction settings.'}
          </p>
        </div>
        <span className="paths-list-inline-form-badge">{isCopyMode ? 'Copy' : 'Offset'}</span>
      </div>
      {!wktAvailable && (
        <p className="paths-list-inline-form-warn">
          WKT coordinate system required — set up in Setup tab.
        </p>
      )}
      <div className="paths-list-inline-form-fields">
        <label className="paths-list-inline-form-field">
          <span>Source path</span>
          <select
            value={sourcePathId}
            onChange={(e) => setSourcePathId(e.target.value)}
            disabled={pathOptions.length === 0 || creating}
          >
            {pathOptions.length === 0 ? (
              <option value="">No routed path available</option>
            ) : (
              pathOptions.map((p) => (
                <option key={p.id} value={p.id}>{p.label}</option>
              ))
            )}
          </select>
        </label>
        <div className="paths-list-inline-form-row">
          <label className="paths-list-inline-form-field">
            <span>Side</span>
            <select value={side} onChange={(e) => setSide(e.target.value)} disabled={creating}>
              <option value="right">Right</option>
              <option value="left">Left</option>
            </select>
          </label>
          <label className="paths-list-inline-form-field">
            <span>Offset (ft)</span>
            <input
              className="paths-list-inline-form-offset-input"
              type="number"
              min="0"
              step="0.1"
              value={offsetFeet}
              onChange={(e) => setOffsetFeet(e.target.value)}
              disabled={creating || isCopyMode}
            />
          </label>
          <label className="paths-list-inline-form-field">
            <span>Direction</span>
            <select value={directionMode} onChange={(e) => setDirectionMode(e.target.value)} disabled={creating}>
              <option value="same">Same</option>
              <option value="reverse">Reverse</option>
            </select>
          </label>
        </div>
      </div>
      <div className="paths-list-inline-form-actions">
        <button
          type="button"
          className="paths-list-inline-form-create"
          onClick={handleCreate}
          disabled={!canCreate || creating}
        >
          <i className={`fas ${creating ? 'fa-spinner fa-spin' : 'fa-solid fa-plus'}`} aria-hidden />
          {creating ? 'Creating…' : 'Create Lane'}
        </button>
        <button type="button" className="paths-list-inline-form-cancel" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── Manual Lane Controls ─────────────────────────────────────────────────────

function InlineManualControls({ pathId, manualLaneCreationActive, manualLanePointCount, onStartManualLane, onStopManualLane, onCancel }) {
  return (
    <div className="paths-list-inline-form paths-list-inline-form--manual">
      <div className="paths-list-inline-form-header">
        <div className="paths-list-inline-form-header-copy">
          <p className="paths-list-inline-form-title">Manual Lane Drawing</p>
          <p className="paths-list-inline-form-subtitle">
            Place lane points directly on the map and finish when the geometry looks right.
          </p>
        </div>
        <span className="paths-list-inline-form-badge">
          {manualLaneCreationActive ? `${manualLanePointCount} pt${manualLanePointCount !== 1 ? 's' : ''}` : 'Manual'}
        </span>
      </div>
      <p className="paths-list-inline-form-hint">
        {manualLaneCreationActive
          ? `Placing points on the map… ${manualLanePointCount} point${manualLanePointCount !== 1 ? 's' : ''} placed`
          : 'Click the map to place lane points, then finish when done.'}
      </p>
      <div className="paths-list-inline-form-actions">
        {!manualLaneCreationActive ? (
          <button
            type="button"
            className="paths-list-inline-form-create"
            onClick={() => onStartManualLane?.(pathId)}
          >
            <i className="fas fa-pen" aria-hidden /> Start placing points
          </button>
        ) : (
          <button
            type="button"
            className="paths-list-inline-form-create"
            onClick={onStopManualLane}
          >
            <i className="fas fa-check-circle" aria-hidden /> Finish Lane
          </button>
        )}
        <button
          type="button"
          className="paths-list-inline-form-cancel"
          onClick={() => { onStopManualLane?.(); onCancel(); }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── Lane Row ─────────────────────────────────────────────────────────────────

const LANE_POSITION_OPTIONS = ['LL', 'CL', 'RL'];
const LANE_DIRECTION_OPTIONS = ['N', 'P'];

function LaneRow({
  lane,
  isActive,
  laneEditMode,
  editingLaneId,
  speedEditMode,
  segmentColors,
  onSelect,
  onDelete,
  onToggleVisible,
  onRename,
  onChangeLanePosition,
  onChangeLaneDirection,
  onEditPoints,
  onExitEditPoints,
  onEnterSpeedMode,
  onExitSpeedMode,
  onUpdateSpeedAtBreakpoint,
  onRemoveSpeedBreakpoint,
}) {
  const color = segmentColors[(lane.colorIndex ?? 1) % segmentColors.length];
  const lenLabel = fmtLength(lane.pathData, lane.waypoints);
  const totalMiles = getLaneLengthMiles(lane);
  const laneTypeLabel = LANE_TYPE_LABELS[lane.laneType] || 'Lane';
  const isThisEditing = laneEditMode && editingLaneId === lane.id;
  const isThisSpeedEditing = speedEditMode && editingLaneId === lane.id;
  const speedSegments = Array.isArray(lane.speedSegments)
    ? [...lane.speedSegments]
        .filter((seg) => Number.isFinite(Number(seg?.mileMark)))
        .sort((a, b) => Number(a.mileMark) - Number(b.mileMark))
    : [];

  const offsetInfo = lane.creationMode === 'offset' && lane.offsetFeet != null
    ? ` · ${lane.offsetFeet} ft ${lane.side ?? ''}`
    : '';

  return (
    <div
      className={`paths-list-lane-row${isActive ? ' paths-list-lane-row--active' : ''}${isThisEditing ? ' paths-list-lane-row--editing' : ''}${isThisSpeedEditing ? ' paths-list-lane-row--speed' : ''}`}
      onClick={() => onSelect(lane.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(lane.id); } }}
      aria-pressed={isActive}
      aria-label={`Select lane ${lane.label}`}
    >
      <div className="paths-list-lane-top">
        <div className="paths-list-lane-info">
        <div className="paths-list-lane-edit-row">
          <InlineLabel
            value={lane.label}
            onChange={(v) => onRename(lane.id, v)}
            forceEdit={isThisEditing}
            leadingIcon={<i className="fa-solid fa-location-dot paths-list-dot" style={{ color }} aria-hidden />}
          />
          {isThisEditing && (
            <>
              <select
                className="paths-list-label-input paths-list-lane-position-select"
                value={lane.lanePosition ?? 'LL'}
                onChange={(e) => onChangeLanePosition?.(lane.id, e.target.value)}
                onClick={(e) => e.stopPropagation()}
              >
                {LANE_POSITION_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
              <select
                className="paths-list-label-input paths-list-lane-position-select paths-list-lane-direction-select"
                value={lane.direction ?? 'P'}
                onChange={(e) => onChangeLaneDirection?.(lane.id, e.target.value)}
                onClick={(e) => e.stopPropagation()}
              >
                {LANE_DIRECTION_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </>
          )}
          {!isThisEditing && (
            <>
              <span className="paths-list-lane-position-badge">{lane.lanePosition ?? 'LL'}</span>
              <span className="paths-list-lane-position-badge">{lane.direction ?? 'P'}</span>
            </>
          )}
        </div>
        <span className="paths-list-meta">
          {laneTypeLabel}{offsetInfo}{lenLabel ? ` · ${lenLabel}` : ''}
          {lane.waypoints?.length > 0 ? ` · ${lane.waypoints.length} pts` : ''}
        </span>
      </div>
      <div className="paths-list-lane-actions" onClick={(e) => e.stopPropagation()}>
        {isActive && !isThisEditing && !isThisSpeedEditing && lane.waypoints?.length > 1 && (
          <button
            type="button"
            className="paths-list-action-btn paths-list-action-btn--edit"
            onClick={() => onEditPoints(lane.id)}
            title="Edit lane points — hold Ctrl to lasso-select"
            aria-label="Edit lane points"
          >
            <i className="fa-solid fa-pen-to-square" aria-hidden />
          </button>
        )}
        {isThisEditing && (
          <button
            type="button"
            className="paths-list-action-btn paths-list-action-btn--done"
            onClick={() => onExitEditPoints()}
            title="Finish editing lane points"
            aria-label="Finish editing"
          >
            <i className="fa-solid fa-check" aria-hidden />
          </button>
        )}
        {isActive && !isThisEditing && !isThisSpeedEditing && lane.waypoints?.length > 1 && (
          <button
            type="button"
            className="paths-list-action-btn paths-list-action-btn--speed"
            onClick={() => onEnterSpeedMode(lane.id)}
            title="Configure speed limits for this lane"
            aria-label="Configure speed"
          >
            <i className="fa-solid fa-gauge-simple-high" aria-hidden />
          </button>
        )}
        {isThisSpeedEditing && (
          <button
            type="button"
            className="paths-list-action-btn paths-list-action-btn--done"
            onClick={() => onExitSpeedMode()}
            title="Finish speed configuration"
            aria-label="Finish speed editing"
          >
            <i className="fa-solid fa-check" aria-hidden />
          </button>
        )}
        <button
          type="button"
          className="paths-panel-waypoints-eye-btn"
          onClick={() => onToggleVisible(lane.id)}
          title={lane.visible ? 'Hide lane' : 'Show lane'}
          aria-label={lane.visible ? 'Hide lane' : 'Show lane'}
        >
          <i className={`fas ${lane.visible ? 'fa-eye' : 'fa-eye-slash'}`} aria-hidden />
        </button>
        <button
          type="button"
          className="paths-panel-waypoints-delete-segment-btn"
          onClick={() => onDelete(lane.id)}
          title="Delete lane"
          aria-label="Delete lane"
        >
          <i className="fa-solid fa-trash-can" aria-hidden />
        </button>
      </div>
      </div>
      {(speedSegments.length > 0 || isThisSpeedEditing) && (
        <div className={`paths-list-speed-summary${isThisSpeedEditing ? ' paths-list-speed-summary--editing' : ''}`}>
          {isThisSpeedEditing && (
            <>
              <div className="paths-list-speed-summary-meta">
                <span className="paths-list-speed-summary-badge">Speed Mode</span>
                <span className="paths-list-speed-summary-total">
                  Total: {totalMiles > 0 ? `${fmtMile(totalMiles)} mi` : '\u2014'}
                </span>
              </div>
              <p className="paths-list-speed-summary-hint">
                Click the lane on the map to add breakpoints, then edit each segment speed directly in the table.
              </p>
            </>
          )}
          {speedSegments.length > 0 ? (
            <table className="paths-list-speed-table">
              <thead>
                <tr>
                  <th>Mileage</th>
                  <th>Speed</th>
                </tr>
              </thead>
              <tbody>
                {speedSegments.map((seg, idx) => {
                  const startMile = Number(seg.mileMark);
                  const nextSeg = speedSegments[idx + 1];
                  const endMile = nextSeg ? Number(nextSeg.mileMark) : totalMiles;
                  const speedMph = Number.isFinite(Number(seg?.speedMph)) && Number(seg.speedMph) > 0
                    ? Number(seg.speedMph)
                    : 45;

                  return (
                    <tr key={`${lane.id}-speed-${idx}`}>
                      <td className="paths-list-speed-table-mileage">
                        {fmtMile(startMile)}-{fmtMile(endMile)} mi
                      </td>
                      <td className="paths-list-speed-table-speed">
                        {isThisSpeedEditing ? (
                          <div className="paths-list-speed-table-speed-editor">
                            <div className="paths-list-speed-table-speed-input-shell">
                              <input
                                type="number"
                                className="paths-list-speed-table-speed-input"
                                value={speedMph}
                                min={1}
                                max={200}
                                onChange={(e) => onUpdateSpeedAtBreakpoint?.(lane.id, idx, Number(e.target.value))}
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => e.stopPropagation()}
                              />
                              <span className="paths-list-speed-table-speed-unit">mph</span>
                            </div>
                            {idx > 0 ? (
                              <button
                                type="button"
                                className="paths-list-speed-table-delete"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onRemoveSpeedBreakpoint?.(lane.id, idx);
                                }}
                                title="Delete this speed segment"
                                aria-label="Delete speed segment"
                              >
                                <i className="fa-solid fa-trash-can" aria-hidden />
                              </button>
                            ) : (
                              <span className="paths-list-speed-table-delete-placeholder" aria-hidden />
                            )}
                          </div>
                        ) : (
                          `${speedMph} mph`
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <p className="paths-list-speed-summary-empty">
              No speed segments yet. Click directly on the lane to add the first breakpoint.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Path Row ─────────────────────────────────────────────────────────────────

function PathRow({
  path,
  lanes,
  allPaths,
  isActive,
  activeLaneId,
  laneEditMode,
  editingLaneId,
  speedEditMode,
  segmentColors,
  wktAvailable,
  manualLaneCreationActive,
  manualLanePointCount,
  onSelect,
  onDelete,
  onToggleVisible,
  onRename,
  onGoTo,
  onSelectLane,
  onDeleteLane,
  onToggleLaneVisible,
  onRenameLane,
  onChangeLanePosition,
  onChangeLaneDirection,
  onEditLanePoints,
  onExitEditLanePoints,
  onEnterSpeedMode,
  onExitSpeedMode,
  onUpdateSpeedAtBreakpoint,
  onRemoveSpeedBreakpoint,
  onAddLane,
  onCreateOffsetLane,
  onStartManualLane,
  onStopManualLane,
  hasMaps2000Folder,
  onCreateOrthoMaps,
  isCreatingOrthoMaps,
  orthoProgress,
}) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  // null | 'picker' | 'offset' | 'copy' | 'manual'
  const [addingLane, setAddingLane] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const color = segmentColors[(path.colorIndex ?? 0) % segmentColors.length];
  const lenLabel = fmtLength(path.pathData, path.waypoints);
  const pathLanes = lanes.filter((l) => l.pathId === path.id);
  const hasPath = Boolean(path.pathData?.geometry?.coordinates?.length);

  // Collapse the picker when this path becomes inactive
  useEffect(() => {
    if (!isActive) setAddingLane(null);
  }, [isActive]);

  function handleAddLaneClick() {
    onAddLane?.(path.id);
    setAddingLane('picker');
  }

  async function handleCreateOffset(args) {
    await onCreateOffsetLane?.(args);
    setAddingLane(null);
  }

  return (
    <div
      className={`paths-list-path-block${isActive ? ' paths-list-path-block--active' : ''}`}
      onClick={() => onSelect(path.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(path.id); } }}
    >
      <div className="paths-list-path-expanded">
        <div className="paths-list-path-header">
          <div className="paths-list-path-info">
            <InlineLabel
              value={path.label}
              onChange={(v) => onRename(path.id, v)}
              leadingIcon={<i className="fa-solid fa-location-dot paths-list-dot" style={{ color }} aria-hidden />}
            />
            <span className="paths-list-meta">
              {path.waypoints?.length ?? 0} points
              {lenLabel ? ` · ${lenLabel}` : ''}
              {pathLanes.length > 0 ? ` · ${pathLanes.length} lane${pathLanes.length !== 1 ? 's' : ''}` : ''}
            </span>
          </div>
          <div className="paths-list-path-actions" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className={`paths-panel-waypoints-goto-btn${!onGoTo || !hasPath ? ' paths-panel-waypoints-goto-btn-disabled' : ''}`}
              onClick={() => {
                if (onGoTo && hasPath) onGoTo(path.id);
              }}
              title={hasPath ? 'Center map on this path' : 'Route this path to enable go to'}
              aria-label="Go to path"
              disabled={!onGoTo || !hasPath}
            >
              <i className="fa-solid fa-location-crosshairs" aria-hidden />
            </button>
            <button
              type="button"
              className="paths-panel-waypoints-eye-btn"
              onClick={() => onToggleVisible(path.id)}
              title={path.visible ? 'Hide path' : 'Show path'}
              aria-label={path.visible ? 'Hide path' : 'Show path'}
            >
              <i className={`fas ${path.visible ? 'fa-eye' : 'fa-eye-slash'}`} aria-hidden />
            </button>
            <button
              type="button"
              className="paths-panel-waypoints-delete-segment-btn"
              onClick={() => setShowDeleteConfirm(true)}
              title="Delete path and its lanes"
              aria-label="Delete path"
            >
              <i className="fa-solid fa-trash-can" aria-hidden />
            </button>
          </div>
        </div>

        {showDeleteConfirm && (
          <div className="delete-confirm-modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
            <div className="delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
              <div className="delete-confirm-modal-header">
                <i className="fas fa-exclamation-triangle"></i>
                <h2>Delete Route?</h2>
              </div>
              <div className="delete-confirm-modal-content">
                <p>
                  Are you sure you want to delete <strong>"{path.label}"</strong> and all its lanes?
                </p>
                {hasMaps2000Folder && (
                  <p className="delete-confirm-warning">
                    This route has ortho maps and Route.json file on Google Drive. These will also be permanently deleted.
                  </p>
                )}
              </div>
              <div className="delete-confirm-modal-footer">
                <button
                  className="delete-confirm-btn cancel-btn"
                  style={{ background: '#0000ff00' }}
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  Cancel
                </button>
                <button
                  className="delete-confirm-btn confirm-btn"
                  onClick={() => { setShowDeleteConfirm(false); onDelete(path.id); }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

      {pathLanes.map((lane) => (
        <LaneRow
          key={lane.id}
          lane={lane}
          isActive={lane.id === activeLaneId}
          laneEditMode={laneEditMode}
          editingLaneId={editingLaneId}
          speedEditMode={speedEditMode}
          segmentColors={segmentColors}
          onSelect={onSelectLane}
          onDelete={onDeleteLane}
          onToggleVisible={onToggleLaneVisible}
          onRename={onRenameLane}
          onChangeLanePosition={onChangeLanePosition}
          onChangeLaneDirection={onChangeLaneDirection}
          onEditPoints={onEditLanePoints}
          onExitEditPoints={onExitEditLanePoints}
          onEnterSpeedMode={onEnterSpeedMode}
          onExitSpeedMode={onExitSpeedMode}
          onUpdateSpeedAtBreakpoint={onUpdateSpeedAtBreakpoint}
          onRemoveSpeedBreakpoint={onRemoveSpeedBreakpoint}
        />
      ))}

      {!laneEditMode && !speedEditMode && (
        <>
          {addingLane === null && (
            hasMaps2000Folder === true ? (
              <button
                type="button"
                className="paths-list-add-lane-btn"
                onClick={handleAddLaneClick}
                disabled={!isActive}
              >
                <i className="fas fa-plus" aria-hidden /> Add Lane
              </button>
            ) : (
              <button
                type="button"
                className={`paths-list-add-lane-btn paths-list-create-ortho-btn${isCreatingOrthoMaps ? ' paths-list-create-ortho-btn--loading' : ''}`}
                onClick={() => onCreateOrthoMaps?.(path.id)}
                disabled={!isActive || isCreatingOrthoMaps}
              >
                {isCreatingOrthoMaps ? (
                  <div className="paths-list-create-ortho-loading-content">
                    <div className="paths-list-create-ortho-loading-label">
                      <span className="paths-list-create-ortho-spinner" />
                      Creating Ortho Maps…
                    </div>
                    <div className="paths-list-create-ortho-progress-track">
                      <div
                        className="paths-list-create-ortho-progress-fill"
                        style={{ width: `${orthoProgress?.progress || 0}%` }}
                      />
                    </div>
                    {orthoProgress?.message && (
                      <span className="paths-list-create-ortho-progress-label">{orthoProgress.message}</span>
                    )}
                  </div>
                ) : (
                  <>
                    <i className="fa-regular fa-map" aria-hidden /> Create Ortho Maps
                  </>
                )}
              </button>
            )
          )}

          {addingLane === 'picker' && (
            <div className="paths-list-lane-picker">
              <div className="paths-list-lane-picker-header">
                <div>
                  <p className="paths-list-lane-picker-title">Choose lane creation mode</p>
                  <p className="paths-list-lane-picker-subtitle">Create a computed offset lane or draw one point-by-point on the map.</p>
                </div>
                <button
                  type="button"
                  className="paths-list-lane-picker-cancel"
                  onClick={() => setAddingLane(null)}
                  aria-label="Cancel lane picker"
                  title="Close lane creation options"
                >
                  <i className="fas fa-times" aria-hidden />
                </button>
              </div>
              <div className="paths-list-lane-picker-options">
                <button
                  type="button"
                  className="paths-list-lane-picker-btn paths-list-lane-picker-btn--offset"
                  onClick={() => setAddingLane('offset')}
                >
                  <div className="paths-list-lane-picker-btn-content">
                    <span className="paths-list-lane-picker-btn-copy">
                      <span className="paths-list-lane-picker-btn-header">
                        <span className="paths-list-lane-picker-btn-header-main">
                          <span className="paths-list-lane-picker-btn-icon" aria-hidden>
                            <i className="fas fa-arrow-right-arrow-left" aria-hidden />
                          </span>
                          <span className="paths-list-lane-picker-btn-title">Offset</span>
                        </span>
                      </span>
                    </span>
                  </div>
                </button>
                <button
                  type="button"
                  className="paths-list-lane-picker-btn paths-list-lane-picker-btn--copy"
                  onClick={() => handleCreateOffset({
                    sourcePathId: path.id,
                    directionMode: 'same',
                    side: 'right',
                    offsetFeet: 0,
                  })}
                >
                  <div className="paths-list-lane-picker-btn-content">
                    <span className="paths-list-lane-picker-btn-copy">
                      <span className="paths-list-lane-picker-btn-header">
                        <span className="paths-list-lane-picker-btn-header-main">
                          <span className="paths-list-lane-picker-btn-icon" aria-hidden>
                            <i className="fas fa-clone" aria-hidden />
                          </span>
                          <span className="paths-list-lane-picker-btn-title">Copy</span>
                        </span>
                      </span>
                    </span>
                  </div>
                </button>
                <button
                  type="button"
                  className="paths-list-lane-picker-btn paths-list-lane-picker-btn--manual"
                  onClick={() => setAddingLane('manual')}
                >
                  <div className="paths-list-lane-picker-btn-content">
                    <span className="paths-list-lane-picker-btn-copy">
                      <span className="paths-list-lane-picker-btn-header">
                        <span className="paths-list-lane-picker-btn-header-main">
                          <span className="paths-list-lane-picker-btn-icon" aria-hidden>
                            <i className="fas fa-pen" aria-hidden />
                          </span>
                          <span className="paths-list-lane-picker-btn-title">Manual</span>
                        </span>
                      </span>
                    </span>
                  </div>
                </button>
              </div>
            </div>
          )}

          {addingLane === 'offset' && (
            <InlineOffsetForm
              paths={allPaths}
              activePathId={path.id}
              wktAvailable={wktAvailable}
              onCreateOffsetLane={handleCreateOffset}
              onCancel={() => setAddingLane(null)}
            />
          )}

          {addingLane === 'copy' && (
            <InlineOffsetForm
              paths={allPaths}
              activePathId={path.id}
              wktAvailable={wktAvailable}
              onCreateOffsetLane={handleCreateOffset}
              onCancel={() => setAddingLane(null)}
              mode="copy"
            />
          )}

          {addingLane === 'manual' && (
            <InlineManualControls
              pathId={path.id}
              manualLaneCreationActive={manualLaneCreationActive}
              manualLanePointCount={manualLanePointCount}
              onStartManualLane={onStartManualLane}
              onStopManualLane={onStopManualLane}
              onCancel={() => { onStopManualLane?.(); setAddingLane(null); }}
            />
          )}
        </>
      )}
      </div>
    </div>
  );
}

// ─── Lane Edit Toolbar ────────────────────────────────────────────────────────

function LaneEditToolbar({ selectedCount, onClearSelection, onExit }) {
  return (
    <div className="paths-list-edit-toolbar">
      <button
        type="button"
        className="paths-list-edit-toolbar-exit"
        onClick={onExit}
        title="Exit lane point edit mode"
      >
        <i className="fas fa-times" aria-hidden /> Exit Edit Mode
      </button>
      <div className="paths-list-edit-toolbar-meta">
        <span className="paths-list-edit-toolbar-count" title="Hold Ctrl and drag to lasso-select points">
          {selectedCount} point{selectedCount !== 1 ? 's' : ''} selected
        </span>
        {selectedCount > 0 && (
          <button
            type="button"
            className="paths-list-edit-toolbar-clear"
            onClick={onClearSelection}
            title="Clear selection"
            aria-label="Clear selection"
          >
            <i className="fas fa-trash" aria-hidden />
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Speed Edit Toolbar ──────────────────────────────────────────────────────

function SpeedEditToolbar({
  lane,
  onUpdateSpeed,
  onRemoveBreakpoint,
  onExit,
}) {
  void lane;
  void onUpdateSpeed;
  void onRemoveBreakpoint;
  void onExit;
  return null;
}

// ─── Main Component ───────────────────────────────────────────────────────────

/**
 * PathsListPanel — Hierarchical list of independent paths and their lanes.
 *
 * Props:
 *   paths, lanes                         — from useRsaPaths state
 *   activePathId, activeLaneId           — active selections
 *   laneEditMode                         — boolean: rubber-band editing active
 *   selectedLaneWaypointIndices          — number[]
 *   segmentColors                        — SEGMENT_COLORS array
 *   wktAvailable                         — boolean: enables offset lane creation
 *   manualLaneCreationActive             — boolean
 *   manualLanePointCount                 — number
 *   onSelectPath, onDeletePath, onTogglePathVisible, onGoToPath, onRenamePath
 *   onSelectLane, onDeleteLane, onToggleLaneVisible, onRenameLane
 *   onAddPath, onAddLane
 *   onEditLanePoints, onExitEditLanePoints
 *   onClearLaneSelection
 *   onCreateOffsetLane                   — ({sourcePathId, directionMode, side, offsetFeet}) => Promise<void>
 *   onStartManualLane                    — (pathId: string) => void
 *   onStopManualLane                     — () => void
 */
export default function PathsListPanel({
  paths = [],
  lanes = [],
  activePathId,
  activeLaneId,
  laneEditMode = false,
  speedEditMode = false,
  selectedLaneWaypointIndices = [],
  segmentColors = SEGMENT_COLORS,
  wktAvailable = false,
  manualLaneCreationActive = false,
  manualLanePointCount = 0,
  onSelectPath,
  onDeletePath,
  onTogglePathVisible,
  onGoToPath,
  onRenamePath,
  onSelectLane,
  onDeleteLane,
  onToggleLaneVisible,
  onRenameLane,
  onChangeLanePosition,
  onChangeLaneDirection,
  onAddPath,
  onAddLane,
  onEditLanePoints,
  onExitEditLanePoints,
  onClearLaneSelection,
  onEnterSpeedMode,
  onExitSpeedMode,
  onUpdateSpeedAtBreakpoint,
  onRemoveSpeedBreakpoint,
  onCreateOffsetLane,
  onStartManualLane,
  onStopManualLane,
  maps2000ByPath = {},
  onCreateOrthoMaps,
  creatingOrthoMapsByPath = {},
  orthoProgressByPath = {},
}) {
  return (
    <div className="paths-list-panel">
      {/* Lane edit toolbar — shown when a lane is in edit mode */}
      {laneEditMode && (
        <LaneEditToolbar
          selectedCount={selectedLaneWaypointIndices.length}
          onClearSelection={onClearLaneSelection}
          onExit={onExitEditLanePoints}
        />
      )}

      {/* Add Path button */}
      {!laneEditMode && !speedEditMode && (
        <button
          type="button"
          className="paths-panel-add-segment-btn"
          onClick={onAddPath}
        >
          <i className="fas fa-plus" aria-hidden /> Add Route
        </button>
      )}

      {/* Empty state */}
      {paths.length === 0 && (
        <div className="paths-list-empty">
          <p>No paths yet.</p>
          <p>Click "Add Route" then click the map to route.</p>
        </div>
      )}

      {/* Path tree */}
      {paths.map((path) => (
        <PathRow
          key={path.id}
          path={path}
          lanes={lanes}
          allPaths={paths}
          isActive={path.id === activePathId}
          activeLaneId={activeLaneId}
          laneEditMode={laneEditMode}
          editingLaneId={activeLaneId}
          speedEditMode={speedEditMode}
          segmentColors={segmentColors}
          wktAvailable={wktAvailable}
          manualLaneCreationActive={manualLaneCreationActive}
          manualLanePointCount={manualLanePointCount}
          onSelect={onSelectPath}
          onDelete={onDeletePath}
          onToggleVisible={onTogglePathVisible}
          onRename={onRenamePath}
          onGoTo={onGoToPath}
          onSelectLane={onSelectLane}
          onDeleteLane={onDeleteLane}
          onToggleLaneVisible={onToggleLaneVisible}
          onRenameLane={onRenameLane}
          onChangeLanePosition={onChangeLanePosition}
          onChangeLaneDirection={onChangeLaneDirection}
          onEditLanePoints={onEditLanePoints}
          onExitEditLanePoints={onExitEditLanePoints}
          onEnterSpeedMode={onEnterSpeedMode}
          onExitSpeedMode={onExitSpeedMode}
          onUpdateSpeedAtBreakpoint={onUpdateSpeedAtBreakpoint}
          onRemoveSpeedBreakpoint={onRemoveSpeedBreakpoint}
          onAddLane={onAddLane}
          onCreateOffsetLane={onCreateOffsetLane}
          onStartManualLane={onStartManualLane}
          onStopManualLane={onStopManualLane}
          hasMaps2000Folder={maps2000ByPath[path.id] ?? null}
          onCreateOrthoMaps={onCreateOrthoMaps}
          isCreatingOrthoMaps={creatingOrthoMapsByPath[path.id] ?? false}
          orthoProgress={orthoProgressByPath[path.id] ?? null}
        />
      ))}
    </div>
  );
}
